import { ErrorBoundaryMeta } from '@liveauctioneers/hammer-ui-core/providers';
import { logger } from '@/utils/logger';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';

/**
 * Centralized error handler for React ErrorBoundary components.
 *
 * This handler:
 * 1. Logs errors to console via pino logger (development)
 * 2. Sends errors to OpenReplay for session replay (production)
 * 3. Tracks critical errors with fatal log level
 * 4. Extracts and structures metadata for debugging
 *
 * @param error - The error that was caught
 * @param errorInfo - React error info containing component stack
 * @param meta - Metadata about the error boundary (name, location, custom fields)
 *
 * @example
 * ```tsx
 * import { handleErrorBoundary } from '@/utils/errorHandling/errorBoundaryHandler';
 * import { HammerUIProvider } from '@liveauctioneers/hammer-ui-core/providers';
 *
 * <HammerUIProvider onError={handleErrorBoundary} theme={theme}>
 *   <App />
 * </HammerUIProvider>
 * ```
 */
export const handleErrorBoundary = (error: Error, errorInfo?: React.ErrorInfo, meta?: ErrorBoundaryMeta): void => {
    const log = logger('ErrorBoundary');

    // Structure error data for logging
    const errorData = {
        boundary: meta,
        componentStack: errorInfo?.componentStack,
        message: error.message,
        name: error.name,
        stack: error.stack,
        timestamp: new Date().toISOString(),
    };

    // Log to console in development for immediate debugging
    if (process.env.NODE_ENV === 'development') {
        log.error({
            ...errorData,
            msg: 'ErrorBoundary caught error',
        });
    }

    // Show toast notification for non-critical errors (user feedback)
    // Critical errors (like root layout failures) don't show toasts - they show full-page error screens
    if (!meta?.critical && typeof window !== 'undefined') {
        toast('error', {
            description: meta?.name ? `${meta.name}: ${error.message}` : error.message,
            title: 'Component Error',
        });
    }

    // Send to OpenReplay in production for session replay
    // Only import and use tracker on client-side to avoid SSR issues
    if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined') {
        // Dynamic import to avoid SSR issues
        import('@/components/OpenReplay/tracker')
            .then(async ({ default: tracker }) => {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive check for dynamic import
                if (tracker) {
                    try {
                        // The tracker may not have been started yet if the error fires during
                        // initial hydration, before the OpenReplay component's useEffect runs.
                        // start() is idempotent — safe to call on an already-active session.
                        if (!tracker.isActive()) {
                            await tracker.start();
                        }
                        tracker.handleError(error, {
                            boundaryLocation: meta?.location,
                            boundaryName: meta?.name,
                            componentStack: errorInfo?.componentStack,
                            metadata: meta,
                        });
                    } catch (openReplayError) {
                        // Don't let OpenReplay errors break the error handler
                        log.error(
                            {
                                err: openReplayError,
                                originalError: error.message,
                            },
                            'Failed to send error to OpenReplay'
                        );
                    }
                }
            })
            .catch((importError) => {
                log.error(
                    {
                        err: importError,
                        originalError: error.message,
                    },
                    'Failed to import OpenReplay tracker'
                );
            });
    }

    // Track critical errors with fatal log level
    // Critical errors typically come from root-level boundaries or important features
    if (meta?.critical) {
        log.fatal({
            ...errorData,
            msg: 'Critical ErrorBoundary triggered',
        });
    }
};
